import { ModalBody, ModalHeader } from "@stenajs-webui/modal";
import { type FC, useEffect } from "react";
import { useParams } from "react-router-dom";

import { Loading } from "../../../../common-ui/Loading";
import { BaseModal } from "../../../../common-ui/modal/BaseModal";
import { useSwitchCandidates } from "../../../../common/graphql/useSwitchCandidates";
import { PermissionGateModal } from "../../../../common/home/components/PermissionGateModal";
import { useAppNavigate } from "../../../../common/hooks/useAppNavigate";
import { usePageTitle } from "../../../../common/routes/PageTitle";
import { trackModalView } from "../../../../common/tracking/trackerService";
import { Permission } from "../../../../services/authentication/utils/authUtils";
import { clear } from "../../../../vendor/suspend-react";
import {
  BOOKING_ERROR_NOT_FOUND,
  BOOKING_ERROR_UNKNOWN,
  BookingErrorModalContents,
} from "../../details/components/BookingErrorModalContents";
import { useActiveRow } from "../../grid/hooks/useActiveRow";
import { SwitchModalContent } from "./SwitchModalContent";

export const BOOKING_WAS_NOT_FOUND_MESSAGE: string = "Booking was not found";

export const SwitchModal: FC = () => {
  const { bookingNo: bookingNoString } = useParams<"bookingNo">();
  const bookingNo = Number(bookingNoString);

  usePageTitle(`Switch ${bookingNo}`);

  const { pop } = useAppNavigate();

  const onRequestClose = () => {
    pop();
    clear(["switch-candidates", bookingNo]);
  };

  useActiveRow({ bookingNo });

  useEffect(() => {
    trackModalView("Switch");
  }, []);

  return (
    <PermissionGateModal
      permission={Permission.EditBookings}
      onRequestClose={onRequestClose}
    >
      <BaseModal
        aria-label={"Switch booking"}
        onRequestClose={onRequestClose}
        shouldCloseOnOverlayClick
      >
        <ModalContent bookingNo={bookingNo} onRequestClose={onRequestClose} />
      </BaseModal>
    </PermissionGateModal>
  );
};

interface ModalContentProps {
  bookingNo: number;
  onRequestClose: () => void;
}

function ModalContent({ bookingNo, onRequestClose }: ModalContentProps) {
  const { booking, candidates, loading, error } =
    useSwitchCandidates(bookingNo);

  if (loading) {
    return (
      <ModalBody>
        <Loading />
      </ModalBody>
    );
  }

  if (error || !booking) {
    const { icon, message } =
      error?.message === BOOKING_WAS_NOT_FOUND_MESSAGE
        ? BOOKING_ERROR_NOT_FOUND
        : BOOKING_ERROR_UNKNOWN;

    return (
      <ModalBody>
        <ModalHeader
          heading={message}
          onClickClose={onRequestClose}
          closeButtonAriaLabel={"Close"}
        />
        <BookingErrorModalContents icon={icon} />
      </ModalBody>
    );
  }

  return (
    <SwitchModalContent
      booking={booking}
      switchCandidates={candidates}
      onRequestClose={onRequestClose}
    />
  );
}
